import { useCallback, useEffect, useRef, useState } from "react";
import type { CreateOrgInvitePayload, OrgInvite, OrgInviteCreateResponse } from "@io-spoke/contracts/orgs";
import { useOrgContext } from "@components/layout/OrgContext";
import { createOrgInvite, listOrgInvites, revokeOrgInvite } from "../lib/orgInvites";
import { useAsyncCallback } from "./useAsyncCallback";

export function useOrgInvitations() {
  const { currentOrg, currentMembership, isSwitching } = useOrgContext();
  const orgId = currentOrg?.id ?? null;
  const canManage = !isSwitching && currentMembership?.orgId === orgId && currentMembership?.status === "active" && ["owner", "admin"].includes(currentMembership.role);
  const scope = useRef({ orgId, canManage, version: 0 });
  if (scope.current.orgId !== orgId || scope.current.canManage !== canManage) {
    scope.current = { orgId, canManage, version: scope.current.version + 1 };
  }
  const [loaded, setLoaded] = useState<{ orgId: string; invites: OrgInvite[] } | null>(null);
  const [created, setCreated] = useState<{ orgId: string; result: OrgInviteCreateResponse } | null>(null);
  const [error, setError] = useState<{ orgId: string; message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const generation = useRef(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; generation.current += 1; }; }, []);
  const isCurrent = (id: string, version = scope.current.version) => mounted.current && scope.current.orgId === id && scope.current.canManage && scope.current.version === version;

  const reload = useCallback(async () => {
    if (!orgId || !canManage) return;
    const request = ++generation.current;
    const version = scope.current.version;
    setLoading(true);
    setError(null);
    try {
      const invites = await listOrgInvites(orgId);
      if (isCurrent(orgId, version) && request === generation.current) setLoaded({ orgId, invites });
    } catch (cause) {
      if (isCurrent(orgId, version) && request === generation.current) setError({ orgId, message: cause instanceof Error ? cause.message : "Unable to load invitations." });
    } finally {
      if (isCurrent(orgId, version) && request === generation.current) setLoading(false);
    }
  }, [orgId, canManage]);
  useEffect(() => {
    setCreated(null);
    setError(null);
    void reload();
    return () => { generation.current += 1; };
  }, [reload]);

  const creating = useAsyncCallback(async (payload: CreateOrgInvitePayload) => {
    if (!orgId || !isCurrent(orgId)) return;
    const version = scope.current.version;
    setError(null);
    setCreated(null);
    try {
      const result = await createOrgInvite(orgId, payload);
      if (!isCurrent(orgId, version)) return;
      setCreated({ orgId, result });
      await reload();
    } catch (cause) {
      if (isCurrent(orgId, version)) setError({ orgId, message: cause instanceof Error ? cause.message : "Unable to create this invitation." });
    }
  });
  const revoking = useAsyncCallback(async (inviteId: string) => {
    if (!orgId || !isCurrent(orgId)) return;
    const version = scope.current.version;
    try {
      const invite = await revokeOrgInvite(orgId, inviteId);
      if (!isCurrent(orgId, version)) return;
      setLoaded((current) => current?.orgId === orgId ? { orgId, invites: current.invites.map((item) => item.id === invite.id ? invite : item) } : current);
      await reload();
    } catch (cause) {
      if (isCurrent(orgId, version)) throw cause;
    }
  });
  const retry = useAsyncCallback(reload);
  return {
    orgId, workspaceName: currentOrg?.name ?? "Workspace", canManage, isSwitching,
    invites: canManage && loaded?.orgId === orgId ? loaded.invites : [],
    created: canManage && created?.orgId === orgId ? created.result : null,
    error: canManage && error?.orgId === orgId ? error.message : null,
    loading: canManage && (loading || (loaded?.orgId !== orgId && error?.orgId !== orgId)),
    create: creating.callback, creating: creating.pending,
    revoke: revoking.callback, revoking: revoking.pending, revokingId: revoking.activeArguments?.[0],
    retry: retry.callback, retrying: retry.pending,
  };
}
